import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UserRound, Eye, Unlock, RefreshCw, History, CheckCircle2, RotateCcw, MessageSquare, StickyNote, ArrowUpRight, Pencil, Trash2, Undo2, AlarmClock, ShieldCheck, XCircle } from "lucide-react";
import { PromptCard } from "@/components/admin/YalolaCards";

// Lot 769 — prise en charge (« le ticket reste pour lui »), présence en direct (« X est dessus »), historique de qui a
// fait quoi, édition des réponses / notes, suppression (super admin).
export const PRESENCE_EVERY_MS = 20000;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

// Battement « je suis sur ce ticket » tant que le détail est ouvert ; renvoie les collègues présents (hors moi).
export const useTicketPresence = (ticketId, enabled) => {
  const [viewers, setViewers] = useState([]);
  const timer = useRef(null);
  useEffect(() => {
    if (!ticketId || !enabled) { setViewers([]); return undefined; }
    let alive = true;
    const beat = () => api.post(`/admin/tickets/${ticketId}/presence`).then(({ data }) => alive && setViewers(data.viewers || [])).catch(() => {});
    beat();
    timer.current = setInterval(beat, PRESENCE_EVERY_MS);
    return () => { alive = false; clearInterval(timer.current); api.delete(`/admin/tickets/${ticketId}/presence`).catch(() => {}); };
  }, [ticketId, enabled]);
  return viewers;
};

// « 👤 Pris en charge par X » — liste (compact) et détail.
export const HandlerChip = ({ ticket, me, compact = false, testId }) => {
  const h = ticket?.handler;
  if (!h) return null;
  const mine = h.id === me;
  return (
    <span className="chip text-[10px] inline-flex items-center gap-1" style={mine ? { background: "#D1FAE5", color: "#065F46" } : { background: "#FEF3C7", color: "#92400E" }} title={`Depuis le ${fmt(h.since)}`} data-testid={testId} data-mine={mine ? "1" : "0"}>
      <UserRound size={10} /> {compact ? (mine ? "À moi" : h.name) : `Pris en charge par ${mine ? "moi" : h.name} · ${fmt(h.since)}`}
    </span>
  );
};

// « 🟢 Marie est dessus » — collègues qui ont le ticket ouvert en ce moment.
export const ViewersChip = ({ viewers, testId }) => {
  if (!viewers?.length) return null;
  const names = viewers.map((v) => v.name).join(", ");
  return (
    <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "#DBEAFE", color: "#1E40AF" }} data-testid={testId} data-count={viewers.length}>
      <span className="h-1.5 w-1.5 rounded-full inline-block" style={{ background: "#16A34A" }} /> <Eye size={10} /> {names} {viewers.length > 1 ? "sont dessus" : "est dessus"}
    </span>
  );
};

// Lot 774 — reprise d'un ticket : directe (super admin, chef de l'équipe de l'agent) ou demande au chef d'équipe (carte
// Yalola, message), que le chef autorise (le ticket passe aussitôt au demandeur) ou refuse.
const TakeoverCard = ({ open, onOpenChange, ticket, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const h = ticket.handler;
  const direct = ticket.can_takeover_direct;
  const leads = (ticket.lead_names || []).join(", ") || "le super admin";
  const submit = async (text) => {
    setBusy(true);
    try {
      if (direct) {
        await api.post(`/admin/tickets/${ticket.id}/claim`, { note: text });
        toast.success(`Ticket repris à ${h.name}`);
      } else {
        await api.post(`/admin/tickets/${ticket.id}/takeover-request`, { message: text });
        toast.success(`Demande envoyée à ${leads}`);
      }
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" ? d.message : d) || e?.friendlyMessage || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <PromptCard open={open} onOpenChange={onOpenChange} busy={busy} onConfirm={submit} testId="ticket-takeover-card"
      icon={direct ? <RefreshCw size={16} style={{ color: "var(--yalola-primary)" }} /> : <ShieldCheck size={16} style={{ color: "var(--yalola-primary)" }} />}
      title={direct ? `Reprendre le ticket à ${h?.name}` : "Demander la reprise au chef d'équipe"}
      description={direct
        ? `Tu deviens responsable de ce ticket ; ${h?.name} est prévenu(e) par une cloche. Note interne facultative pour le fil.`
        : `Ce ticket est tenu par ${h?.name}. Seul son chef d'équipe (${leads}) peut autoriser la reprise : explique-lui pourquoi tu veux reprendre ce ticket.`}
      placeholder={direct ? "Note interne (facultative)…" : "Message au chef d'équipe (ex. : le membre m'a écrit en privé, je connais ce dossier…)"}
      confirmLabel={direct ? "Reprendre le ticket" : "Envoyer la demande"} required={!direct}>
      {!direct && (
        <div className="rounded-xl px-3 py-2 text-xs flex items-center gap-2" style={{ background: "#FFFBEB", color: "#92400E" }} data-testid="ticket-takeover-card-lead">
          <UserRound size={12} /> Chef d'équipe : <b>{leads}</b> — il reçoit une cloche et décide depuis le ticket.
        </div>
      )}
    </PromptCard>
  );
};

// Demande en attente : vue du demandeur (retirer), du chef (autoriser / refuser) ou des autres agents (lecture).
const TakeoverRequestCard = ({ ticket, me, isSuper, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [refusing, setRefusing] = useState(false);
  const r = ticket.takeover_request;
  if (!r || r.status !== "pending" || ticket.status !== "open") return null;
  const mine = r.by === me;
  const lead = isSuper || (r.lead_ids || []).includes(me);
  const decide = async (approve, note = "") => {
    setBusy(true);
    try {
      await api.post(`/admin/tickets/${ticket.id}/takeover-request/decide`, { approve, note });
      toast.success(approve ? `Reprise autorisée : le ticket passe à ${r.by_name}` : "Reprise refusée");
      setRefusing(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { await api.delete(`/admin/tickets/${ticket.id}/takeover-request`); toast.success("Demande retirée"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl px-3 py-2.5 text-xs space-y-2" style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", color: "#3730A3" }} data-testid="ticket-takeover-request" data-mine={mine ? "1" : "0"} data-lead={lead ? "1" : "0"}>
      <div className="flex items-start gap-2 flex-wrap">
        <ShieldCheck size={14} className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <b>{mine ? "Ta demande de reprise" : `${r.by_name} demande à reprendre ce ticket`}</b> {mine ? `est en attente de ${(r.lead_names || []).join(", ") || "super admin"}` : `à ${r.from_name}`} · {fmt(r.at)}
          {r.message && <div className="mt-0.5 italic" data-testid="ticket-takeover-request-msg">« {r.message} »</div>}
        </div>
        {mine && <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5" disabled={busy} onClick={cancel} data-testid="ticket-takeover-request-cancel">Retirer</Button>}
        {!mine && lead && !refusing && (
          <span className="flex gap-1.5">
            <Button size="sm" className="pill-primary rounded-full h-7 px-2.5" disabled={busy} onClick={() => decide(true)} data-testid="ticket-takeover-approve"><CheckCircle2 size={12} className="mr-1" /> Autoriser</Button>
            <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5" disabled={busy} onClick={() => setRefusing(true)} data-testid="ticket-takeover-refuse"><XCircle size={12} className="mr-1" /> Refuser</Button>
          </span>
        )}
        {!mine && !lead && <span className="chip text-[10px]" style={{ background: "#E0E7FF", color: "#3730A3" }} data-testid="ticket-takeover-request-waiting">en attente du chef d'équipe</span>}
      </div>
      <PromptCard open={refusing} onOpenChange={setRefusing} busy={busy} onConfirm={(note) => decide(false, note)} testId="ticket-takeover-refuse-card"
        icon={<XCircle size={16} style={{ color: "#B91C1C" }} />} title={`Refuser la reprise demandée par ${r.by_name}`}
        description={`${r.from_name} garde le ticket. Une note (facultative) est envoyée à ${r.by_name} avec le refus.`} placeholder="Note pour le demandeur (facultative)…" confirmLabel="Refuser la reprise" />
    </div>
  );
};

// Barre du détail : qui est dessus + prendre en charge / reprendre (ou demander) / libérer.
export const OwnershipBar = ({ ticket, me, isSuper, canManage, viewers, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [takeoverOpen, setTakeoverOpen] = useState(false);
  if (!ticket || ticket.status !== "open") return null;
  const h = ticket.handler;
  const mine = h?.id === me;
  const pending = ticket.takeover_request?.status === "pending";
  const direct = ticket.can_takeover_direct !== false;
  const call = async (path, ok, body) => {
    setBusy(true);
    try { await api.post(`/admin/tickets/${ticket.id}/${path}`, body); toast.success(ok); onChanged?.(); }
    catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap text-xs" style={{ background: h && !mine ? "#FFFBEB" : "var(--yalola-surface-alt)", border: "1px solid var(--yalola-border)" }} data-testid="ticket-ownership-bar">
        <HandlerChip ticket={ticket} me={me} testId="ticket-handler-chip" />
        {h && !mine && ticket.team_leads_on && ticket.lead_names?.length > 0 && (
          <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "#E0E7FF", color: "#3730A3" }} title="Chef d'équipe de l'agent qui tient le ticket" data-testid="ticket-lead-chip"><ShieldCheck size={10} /> Chef : {ticket.lead_names.join(", ")}</span>
        )}
        {!h && <span style={{ color: "var(--yalola-text-muted)" }} data-testid="ticket-no-handler">Personne ne s'en occupe encore — ta première réponse te l'attribue.</span>}
        {h && !mine && <span style={{ color: "#92400E" }} data-testid="ticket-locked-hint">{direct ? "Pour répondre au membre, reprends d'abord le ticket (notes internes libres)." : "Pour répondre au membre, demande la reprise à son chef d'équipe (notes internes libres)."}</span>}
        <ViewersChip viewers={viewers} testId="ticket-viewers-chip" />
        {canManage && (
          <span className="ml-auto flex gap-1.5">
            {!h && <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5" disabled={busy} onClick={() => call("claim", "Ticket pris en charge")} data-testid="ticket-claim-btn"><UserRound size={12} className="mr-1" /> Prendre en charge</Button>}
            {h && !mine && !pending && (
              <Button size="sm" className="pill-primary rounded-full h-7 px-2.5" disabled={busy} onClick={() => setTakeoverOpen(true)} data-testid="ticket-takeover-btn" data-direct={direct ? "1" : "0"}>
                {direct ? <RefreshCw size={12} className="mr-1" /> : <ShieldCheck size={12} className="mr-1" />} {direct ? "Reprendre le ticket" : "Demander la reprise"}
              </Button>
            )}
            {h && (mine || isSuper) && <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5" disabled={busy} onClick={() => call("release", "Ticket libéré")} data-testid="ticket-release-btn"><Unlock size={12} className="mr-1" /> Libérer</Button>}
          </span>
        )}
      </div>
      <TakeoverRequestCard ticket={ticket} me={me} isSuper={isSuper} onChanged={onChanged} />
      {h && !mine && <TakeoverCard open={takeoverOpen} onOpenChange={setTakeoverOpen} ticket={ticket} onChanged={onChanged} />}
    </div>
  );
};

const KIND = {
  reply: { label: "a répondu au membre", Icon: MessageSquare, color: "var(--yalola-primary)" },
  note: { label: "a laissé une note interne", Icon: StickyNote, color: "#6D28D9" },
  claim: { label: "a pris le ticket en charge", Icon: UserRound, color: "#047857" },
  takeover: { label: "a repris le ticket", Icon: RefreshCw, color: "#B45309" },
  release: { label: "a libéré le ticket", Icon: Unlock, color: "#6B7280" },
  assign: { label: "a assigné le ticket", Icon: ArrowUpRight, color: "#5B21B6" },
  transfer: { label: "a transféré le ticket", Icon: ArrowUpRight, color: "#5B21B6" },
  escalation_cancel: { label: "a repris le ticket", Icon: Undo2, color: "#5B21B6" },
  resolve: { label: "a marqué le ticket résolu", Icon: CheckCircle2, color: "#15803D" },
  reopen: { label: "a réouvert le ticket", Icon: RotateCcw, color: "#B45309" },
  edit: { label: "a modifié une réponse", Icon: Pencil, color: "#6B7280" },
  edit_note: { label: "a modifié une note", Icon: Pencil, color: "#6B7280" },
  delete_message: { label: "a supprimé un message", Icon: Trash2, color: "#B91C1C" },
  stale_alert: { label: "a relancé (ticket oublié)", Icon: AlarmClock, color: "#C2410C" },   // lot 770 — « Système »
  takeover_request: { label: "a demandé la reprise au chef d'équipe", Icon: ShieldCheck, color: "#4338CA" },   // lot 774
  takeover_approved: { label: "a autorisé la reprise", Icon: CheckCircle2, color: "#047857" },
  takeover_refused: { label: "a refusé la reprise", Icon: XCircle, color: "#B91C1C" },
};

// Historique « qui a fait quoi » + résumé « a répondu : Marie (3), Paul (1) ».
export const TicketHistory = ({ ticket }) => {
  const [open, setOpen] = useState(false);
  const agents = ticket?.agents || [];
  const history = ticket?.history || [];
  return (
    <div className="yalola-card p-4 space-y-2" data-testid="ticket-history">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <History size={14} style={{ color: "var(--yalola-primary)" }} />
        <b>Historique</b>
        {agents.length > 0 ? (
          <span data-testid="ticket-agents">· ont répondu : {agents.map((a) => `${a.name || "équipe"} (${a.replies})`).join(", ")}</span>
        ) : <span style={{ color: "var(--yalola-text-muted)" }} data-testid="ticket-agents-none">· aucune réponse de l'équipe pour l'instant</span>}
        <button type="button" className="ml-auto chip text-[10px]" onClick={() => setOpen((v) => !v)} data-testid="ticket-history-toggle">{open ? "Masquer" : `Voir (${history.length})`}</button>
      </div>
      {open && (
        <ol className="space-y-1.5 text-xs" data-testid="ticket-history-list">
          {history.length === 0 && <li style={{ color: "var(--yalola-text-muted)" }}>Rien pour l'instant.</li>}
          {history.map((ev, i) => {
            const k = KIND[ev.kind] || { label: ev.kind, Icon: History, color: "var(--yalola-text-muted)" };
            const Icon = k.Icon;
            return (
              <li key={`${ev.at}-${i}`} className="flex items-start gap-2" data-testid={`ticket-history-${ev.kind}`}>
                <Icon size={12} className="mt-0.5 shrink-0" style={{ color: k.color }} />
                <span className="min-w-0"><b>{ev.by_name || "équipe"}</b> {k.label}{ev.detail ? <span style={{ color: "var(--yalola-text-muted)" }}> — {ev.detail}</span> : null}</span>
                <span className="ml-auto shrink-0 tabular-nums" style={{ color: "var(--yalola-text-muted)" }}>{fmt(ev.at)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
